import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Pencil, Trash2, Check, X, Volume2, RefreshCw } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { vocabularyApi } from '@/api/vocabulary'
import { adminApi } from '@/api/admin'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Label } from '@/components/ui/Label'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { ErrorScreen } from '@/components/ErrorScreen'
import { CefrBadge } from '@/components/CefrBadge'
import { AiExplanationPanel } from '@/components/ai/AiExplanationPanel'
import { MorphologyBlock } from '@/components/vocabulary/MorphologyBlock'
import { WordLearningSection } from '@/components/vocabulary/WordLearningSection'
import { KnownMeaningsBlock } from '@/components/vocabulary/KnownMeaningsBlock'
import { LearnAnotherMeaningPanel } from '@/components/vocabulary/LearnAnotherMeaningPanel'
import { cn } from '@/components/ui/cn'
import { copy } from '@/lib/copy'
import { inputLimits } from '@/lib/inputLimits'
import { playPronunciation } from '@/lib/playPronunciation'
import { useIsAdmin } from '@/hooks/useIsAdmin'
import type { WordStatus } from '@/types/common'

const STATUS_OPTIONS: WordStatus[] = ['NEW', 'LEARNING', 'REVIEW', 'MASTERED']

export function WordDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const isAdmin = useIsAdmin()
  const [isEditing, setIsEditing] = useState(false)
  const [reEnrichStatus, setReEnrichStatus] = useState<'idle' | 'loading' | 'done' | 'error'>('idle')

  const { data: word, isLoading, isError, refetch } = useQuery({
    queryKey: ['vocabulary', id],
    queryFn: () => vocabularyApi.getWordById(id!).then((r) => r.data),
    enabled: !!id,
  })

  const { register, handleSubmit, reset } = useForm<{
    translation: string
    personalNotes: string
    status: WordStatus
  }>()

  const updateMutation = useMutation({
    mutationFn: (data: { translation: string; personalNotes: string; status: WordStatus }) =>
      vocabularyApi.updateWord(id!, data),
    onSuccess: (res) => {
      queryClient.setQueryData(['vocabulary', id], res.data)
      queryClient.invalidateQueries({ queryKey: ['vocabulary'] })
      setIsEditing(false)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => vocabularyApi.deleteWord(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vocabulary'] })
      navigate('/vocabulary', { replace: true })
    },
  })

  const handleReEnrich = async () => {
    if (!word || !window.confirm(`Re-enrich "${word.word}"? This will reset the platform-wide dictionary data and re-run AI enrichment for all languages.`)) return
    setReEnrichStatus('loading')
    try {
      await adminApi.reEnrichWord(word.wordId)
      await queryClient.invalidateQueries({ queryKey: ['vocabulary', id] })
      setReEnrichStatus('done')
      setTimeout(() => setReEnrichStatus('idle'), 3000)
    } catch {
      setReEnrichStatus('error')
      setTimeout(() => setReEnrichStatus('idle'), 3000)
    }
  }

  const startEdit = () => {
    if (!word) return
    reset({
      translation: word.translation,
      personalNotes: word.personalNotes ?? '',
      status: word.status,
    })
    setIsEditing(true)
  }

  const submitEdit = handleSubmit((data) => {
    if (!word) {
      updateMutation.mutate(data)
      return
    }
    if (
      data.status === 'MASTERED' &&
      word.status !== 'MASTERED' &&
      (word.intervalDays < 21 || word.reviewCount < 3) &&
      !window.confirm(copy.word.masteredWarning)
    ) {
      return
    }
    updateMutation.mutate(data)
  })

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoadingSpinner />
      </div>
    )
  }

  if (isError) {
    return (
      <ErrorScreen
        variant="network"
        title="Couldn't load word"
        description="Please check your connection and try again."
        onRetry={() => refetch()}
      />
    )
  }

  if (!word) {
    return <ErrorScreen variant="not-found" title="Word not found" showHomeLink />
  }

  return (
    <div className="mx-auto max-w-2xl">
      <button
        onClick={() => navigate('/vocabulary')}
        className="mb-4 flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to vocabulary
      </button>

      {/* Hero: word + translation + SRS at a glance */}
      <div className="mb-6 rounded-xl border bg-card p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold tracking-tight">{word.word}</h1>
              <button
                type="button"
                onClick={() => playPronunciation(word.word, word.audioUrl)}
                className="rounded-full p-2 text-primary hover:bg-primary/10"
                aria-label="Play pronunciation"
              >
                <Volume2 className="h-5 w-5" />
              </button>
            </div>
            {word.phonetic && (
              <p className="mt-1 text-sm text-muted-foreground">{word.phonetic}</p>
            )}
            <div className="mt-2 flex flex-wrap gap-2">
              {word.partOfSpeech && (
                <span className="text-sm italic text-muted-foreground">{word.partOfSpeech}</span>
              )}
              {word.unitType && word.unitType !== 'WORD' && (
                <span className="rounded-full bg-secondary px-2 py-0.5 text-xs font-medium capitalize">
                  {word.unitType.toLowerCase().replace('_', ' ')}
                </span>
              )}
              {word.cefrLevel && <CefrBadge level={word.cefrLevel} />}
              {word.knowledgeConfidence != null && word.knowledgeConfidence < 0.75 && (
                <span
                  className="rounded-full border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-xs font-medium text-amber-700 dark:text-amber-300"
                  title={copy.word.aiGeneratedHint}
                >
                  {copy.word.aiGenerated}
                </span>
              )}
            </div>
          </div>
          <div className="flex shrink-0 gap-2">
            {!isEditing && (
              <>
                {isAdmin && (
                  <button
                    onClick={handleReEnrich}
                    disabled={reEnrichStatus === 'loading'}
                    className={cn(
                      'rounded-md border p-2 text-muted-foreground hover:bg-accent hover:text-foreground',
                      reEnrichStatus === 'done' && 'border-green-500/40 text-green-600 dark:text-green-400',
                      reEnrichStatus === 'error' && 'border-destructive/40 text-destructive',
                      reEnrichStatus === 'loading' && 'cursor-not-allowed opacity-50',
                    )}
                    title={
                      reEnrichStatus === 'done'
                        ? 'Re-enriched'
                        : reEnrichStatus === 'error'
                          ? 'Re-enrichment failed'
                          : 'Re-enrich word (admin)'
                    }
                    aria-label="Re-enrich word"
                  >
                    <RefreshCw className={cn('h-4 w-4', reEnrichStatus === 'loading' && 'animate-spin')} />
                  </button>
                )}
                <button
                  onClick={startEdit}
                  className="rounded-md border p-2 text-muted-foreground hover:bg-accent hover:text-foreground"
                  aria-label="Edit"
                >
                  <Pencil className="h-4 w-4" />
                </button>
                <button
                  onClick={() => deleteMutation.mutate()}
                  className="rounded-md border p-2 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                  aria-label="Delete"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </>
            )}
          </div>
        </div>

        {isEditing ? (
          <form className="mt-5 space-y-4 border-t border-border/60 pt-5" onSubmit={submitEdit}>
            <div className="space-y-1">
              <Label>Translation</Label>
              <Input {...register('translation')} maxLength={inputLimits.translation} />
            </div>
            <div className="space-y-1">
              <Label>Status</Label>
              <select
                className="w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                {...register('status')}
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <Label>Personal notes</Label>
              <Input {...register('personalNotes')} maxLength={inputLimits.personalNotes} />
            </div>
            <div className="flex gap-2">
              <Button type="submit" className="flex-1" loading={updateMutation.isPending}>
                <Check className="h-4 w-4" /> Save
              </Button>
              <Button
                type="button"
                className="bg-secondary text-secondary-foreground hover:bg-secondary/80"
                onClick={() => setIsEditing(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </form>
        ) : (
          <>
            <p
              className="mt-5 text-2xl font-semibold leading-snug text-foreground"
              data-testid="word-translation"
            >
              {word.translation}
            </p>
            {word.personalNotes && (
              <p className="mt-2 text-sm italic text-muted-foreground">
                &ldquo;{word.personalNotes}&rdquo;
              </p>
            )}
          </>
        )}
      </div>

      <KnownMeaningsBlock
        knownSenses={word.knownSenses ?? []}
        localizedSenses={word.localizedSenses ?? []}
        primarySenseKey={word.senseKey}
        userWordId={word.id}
      />

      <LearnAnotherMeaningPanel word={word} />

      <MorphologyBlock userWordId={word.id} />

      {word.examples.length > 0 && (
        <Section title="Examples">
          <ul className="space-y-3">
            {word.examples.map((ex, i) => (
              <li key={i} className="rounded-lg border border-border/60 bg-muted/20 px-3 py-2">
                {ex.english && (
                  <p className="text-sm text-foreground">&ldquo;{ex.english}&rdquo;</p>
                )}
                {ex.translation && (
                  <p className={cn('text-sm text-muted-foreground', ex.english && 'mt-1')}>
                    {ex.english ? ex.translation : `"${ex.translation}"`}
                  </p>
                )}
              </li>
            ))}
          </ul>
        </Section>
      )}

      <AiExplanationPanel wordId={word.wordId} />

      <WordLearningSection word={word} />

      {word.collections.length > 0 && (
        <Section title="Collections">
          <div className="flex flex-wrap gap-2">
            {word.collections.map((c) => (
              <span key={c.id} className="rounded-full bg-secondary px-3 py-1 text-xs font-medium">
                {c.name}
              </span>
            ))}
          </div>
        </Section>
      )}
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-4 rounded-xl border bg-card p-4">
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</h3>
      {children}
    </div>
  )
}
